import { isValid as isValidCpf } from "@fnando/cpf";

export interface DocumentType {
  id: string;
  name: string;
  mask: string | null;
}

/**
 * Aplica máscara ao documento baseado no tipo
 */
export function applyDocumentMask(value: string, documentType?: DocumentType): string {
  const alphanumeric = value.replace(/[^a-zA-Z0-9]/g, "");

  if (!documentType) {
    return alphanumeric;
  }

  if (documentType.mask) {
    // Para RG, permite 'X' como último caractere (dígito verificador)
    let processedValue: string;
    if (documentType.name.toUpperCase() === 'RG') {
      // Aceita dígitos e X/x no último caractere
      processedValue = alphanumeric.replace(/[^0-9Xx]/g, "");
      // Converte 'x' minúsculo para 'X' maiúsculo
      processedValue = processedValue.replace(/x/g, "X");
    } else {
      // Para outros documentos, apenas dígitos
      processedValue = alphanumeric.replace(/\D/g, "");
    }

    const maxDigits = (documentType.mask.match(/0/g) ?? []).length;
    const trimmedValue = processedValue.slice(0, maxDigits);

    let formatted = "";
    let digitIndex = 0;

    for (const char of documentType.mask) {
      if (char === "0") {
        if (digitIndex >= trimmedValue.length) {
          break;
        }
        formatted += trimmedValue[digitIndex] ?? "";
        digitIndex += 1;
      } else {
        if (digitIndex < trimmedValue.length) {
          formatted += char;
        } else {
          break;
        }
      }
    }

    return formatted;
  }

  return alphanumeric.toUpperCase().slice(0, 15);
}

/**
 * Remove máscara do documento (retorna apenas dígitos, exceto X para RG)
 */
export function removeDocumentMask(value: string, documentType?: DocumentType): string {
  // Para RG, preserva o 'X' como dígito verificador
  if (documentType?.name.toUpperCase() === 'RG') {
    return value.replace(/[^0-9Xx]/g, "").replace(/x/g, "X");
  }
  // Para outros documentos, apenas dígitos
  return value.replace(/\D/g, "");
}

/**
 * Valida documento baseado no tipo
 */
export function validateDocument(value: string, documentType?: DocumentType): {
  isValid: boolean;
  error?: string;
} {
  if (!documentType || !value) {
    return { isValid: true };
  }

  const cleanValue = removeDocumentMask(value, documentType);

  // Validação específica por tipo
  switch (documentType.name.toUpperCase()) {
    case 'CPF':
      if (!isValidCpf(cleanValue)) {
        return { isValid: false, error: 'CPF inválido' };
      }
      break;

    case 'RG':
      // RG aceita 7-9 caracteres, sendo o último podendo ser 'X' (quando DV = 10)
      if (cleanValue.length < 7 || cleanValue.length > 9) {
        return { isValid: false, error: 'RG deve ter entre 7 e 9 caracteres' };
      }
      // Valida formato: N dígitos seguido opcionalmente de X no final
      if (!/^[0-9]{6,8}[0-9X]?$/.test(cleanValue)) {
        return { isValid: false, error: 'RG deve conter apenas dígitos, podendo terminar com X' };
      }
      break;

    case 'CNH':
      if (cleanValue.length !== 11) {
        return { isValid: false, error: 'CNH deve ter 11 dígitos' };
      }
      break;

    case 'PASSAPORTE':
      // Passaporte aceita alfanumérico, sem validação específica
      if (cleanValue.length < 6 || cleanValue.length > 15) {
        return { isValid: false, error: 'Passaporte deve ter entre 6 e 15 caracteres' };
      }
      break;

    default:
      return { isValid: true };
  }

  return { isValid: true };
}

/**
 * Retorna placeholder do documento baseado no tipo
 */
export function getDocumentPlaceholder(documentType?: DocumentType): string {
  if (documentType?.mask) return documentType.mask;
  if (!documentType) return "Selecione o tipo de documento";
  return "Documento";
}

/**
 * Retorna maxLength do input baseado no tipo
 */
export function getDocumentMaxLength(documentType?: DocumentType): number {
  if (documentType?.mask) {
    return documentType.mask.length;
  }
  return 15;
}
